// Re-point an open chart to another visit of the same patient, the way a
// dashboard card click does (10-06, patient 4662: "record on my own visit"
// from the recording start question).
//
// The chart lives in the dashboard (brainycarehome: selectedCard -> pmodal ->
// PateintsChartsWrapper -> SideFrame `data`). Every chart open goes through
// its openChart(card); this module only finds the visit's dashboard card and
// hands it to that same function:
//   recording side  - requestChartVisit({ patientid, appointmentid }) emits
//                     OPEN_CHART_VISIT and resolves the card the chart now
//                     shows, or null;
//   dashboard side  - openChartVisitCard(target, { rows, searchToday,
//                     openChart }) finds the card (the loaded day first, else
//                     today's patient search - the chart resolver's own call)
//                     and calls openChart(card); null unless the chart then
//                     shows that very visit.

import { eventBus } from "./eventBus";
import { isCancelledVisit, rowsOfBody, sameAppointmentId, samePatient } from "./visitGuard";

export const OPEN_CHART_VISIT = "openChartVisit";

// The dashboard card of one visit: same patient and appointment, not cancelled.
export const findVisitCard = (rows, patientid, appointmentid) =>
  (Array.isArray(rows) ? rows : []).find(
    (r) =>
      r &&
      samePatient(r.patientid, patientid) &&
      sameAppointmentId(r.appointmentid, appointmentid) &&
      !isCancelledVisit(r),
  ) || null;

export const openChartVisitCard = async ({ patientid, appointmentid } = {}, { rows, searchToday, openChart } = {}) => {
  let card = findVisitCard(rows, patientid, appointmentid);
  if (!card && typeof searchToday === "function") {
    try {
      card = findVisitCard(rowsOfBody(await searchToday(patientid)), patientid, appointmentid);
    } catch (_) {
      card = null;
    }
  }
  if (!card || typeof openChart !== "function") return null;
  // openChart answers with the card it showed (true from an older caller).
  // Its resolver may open ANOTHER visit than the one asked for (a card it
  // does not classify as today's): the recording must then not start, or it
  // would go to one visit while the chart shows another.
  const opened = await openChart(card);
  if (!opened) return null;
  const shown = opened === true ? card : opened;
  return sameAppointmentId(shown?.appointmentid, appointmentid) ? shown : null;
};

// null when no dashboard is listening (the chart is not open there), the
// visit's card cannot be found, a newer chart open superseded it, or nothing
// answered within `timeout`.
export const requestChartVisit = ({ patientid, appointmentid } = {}, { timeout = 15000 } = {}) =>
  new Promise((resolve) => {
    if (!eventBus.listenerCount(OPEN_CHART_VISIT)) {
      resolve(null);
      return;
    }
    let done = false;
    let timer = null;
    const respond = (card) => {
      if (done) return;
      done = true;
      if (timer) clearTimeout(timer);
      resolve(card || null);
    };
    timer = setTimeout(() => respond(null), timeout);
    eventBus.emit(OPEN_CHART_VISIT, { patientid, appointmentid, respond });
  });
